import FlagLanderBanner from '@acorn-portal/shared/src/components/banner/FlagLanderBanner';
import { Box, Button, Divider, Fade, Paper, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { format } from 'date-fns';
import { DISPLAY_DATE_TIME } from '@acorn-portal/shared/src/utils/constants';
import { ListNames } from '@acorn-portal/shared/src/store/referenceData.slice';
import useReferenceData from '@acorn-portal/shared/src/hooks/useReferenceData';
import { Home, VerifiedUser } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import Vehicle from '@acorn-portal/shared/src/components/display/vehicle';
import AddressDetails from '@acorn-portal/shared/src/components/renewal/address';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import SortDrivers from '@acorn-portal/shared/src/utils/sortDrivers';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { green } from '@mui/material/colors';
import { useEffect } from 'react';
import { setMtaPurchased } from '@acorn-portal/shared/src/store/midTermAdjustment.slice';

const ChangeSuccess = () => {
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const midTermAdjustment = useAppSelector((redux) => redux.midTermAdjustment);
	const contactPreferences = useAppSelector((redux) => redux.contactPreferences);
	const { getReferenceDataValue } = useReferenceData();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const isFutureDated: boolean = midTermAdjustment.isMtaFutureDated;

	useEffect(() => {
		dispatch(setMtaPurchased(true));
	}, []);

	const { additionalDrivers, policyHolder } = SortDrivers(
		midTermAdjustment.drivers.map((driver) => ({
			...driver,
			dataPointId: driver.dataPointId ?? '',
			lastName: driver.lastNames,
			isPolicyholder: driver.relationshipToProposer === 'P',
			mainDriver: driver.isMainDriver,
		})),
	);

	return (
		<Paper
			elevation={1}
			sx={{
				borderRadius: '8px',
			}}
		>
			<Fade in>
				<Box>
					<Box>
						<FlagLanderBanner
							text={
								<Box
									sx={(theme) => ({
										[theme.breakpoints.down('md')]: {
											fontSize: '1.4rem',
											width: '80%',
										},
										[theme.breakpoints.down('sm')]: {
											fontSize: '1.1rem',
											width: '65%',
										},
									})}
								>
									Your change has been successful!
								</Box>
							}
						/>
						<Box
							sx={{
								margin: 2,
								display: 'flex',
								alignItems: 'center',
								flexDirection: 'column',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'start',
									p: 2,
									borderRadius: 2,
									backgroundColor: green[50],
									my: 2,
								}}
							>
								<CheckCircleIcon sx={{ color: green[500], marginRight: 1, fontSize: '32px' }} />
								<Typography
									sx={{
										fontWeight: 400,
										fontSize: '18px',
										color: green[500],
									}}
								>
									{isFutureDated
										? `Your change has been successful with the effective date of ${format(
												new Date(midTermAdjustment.mtaStartDate),
												DISPLAY_DATE_TIME,
											)}`
										: 'Your change has been successful and will be active within 15 minutes.'}
								</Typography>
							</Box>
							<Box sx={{ maxWidth: 700, width: '100%' }}>
								<Typography variant='h5' color='secondary' sx={{ marginBottom: 3, fontWeight: 'bold' }}>
									New policy details
								</Typography>

								<Typography
									sx={{
										marginBottom: 2,
										display: 'flex',
										alignItems: 'center',
									}}
								>
									<VerifiedUser sx={{ marginRight: 1 }} />{' '}
									{getReferenceDataValue(
										ListNames.levelOfCoverTypes,
										midTermAdjustment.vehicleDetails.coverDefinitionCode ?? '',
									)}
								</Typography>

								<Vehicle
									product={fullPolicy.product}
									vehicle={{
										...midTermAdjustment.vehicleDetails,
										registrationNumber: midTermAdjustment.vehicleDetails.registration,
									}}
								/>
								<Divider sx={{ mb: 2 }} />
								<AddressDetails
									name={`${policyHolder.firstName} ${policyHolder.lastName}`}
									address={midTermAdjustment.policyDetails.policyholder.address}
									namedDrivers={additionalDrivers}
								/>
							</Box>
						</Box>
						<Divider />
						<Typography sx={{ margin: 'auto', textAlign: 'center', p: 2 }}>
							Please return to the Policy Screen to see your updated policy documents. It is important you take the time
							to carefully read this information.
						</Typography>
						{contactPreferences.printToPost && (
							<Typography sx={{ margin: 'auto', textAlign: 'center', p: 2 }}>
								We&apos;ve also sent your updated documents in the post, they will arrive in 3-5 working days.
							</Typography>
						)}
					</Box>
					<Box
						sx={{
							padding: 3,
							textAlign: 'center',
							maxWidth: 400,
							margin: 'auto',
						}}
					>
						<Button
							startIcon={<Home />}
							size='large'
							variant='contained'
							onClick={() => {
								navigate(ROUTE_PATHS.home);
								return null;
							}}
						>
							Return to Policy Screen
						</Button>
					</Box>
				</Box>
			</Fade>
		</Paper>
	);
};

export default ChangeSuccess;
